import { z } from "zod";

import { normalizeLivekitTimestampMs } from "@/shared/agent-api/livekit-timestamps";

export enum ChatItemRole {
  ASSISTANT = "assistant",
  SYSTEM = "system",
  USER = "user",
}

export enum ChatItemType {
  FUNCTION_CALL = "function_call",
  FUNCTION_CALL_OUTPUT = "function_call_output",
  MESSAGE = "message",
}

export enum EventType {
  CONVERSATION_ITEM_ADDED = "conversation_item_added",
  FUNCTION_TOOLS_EXECUTED = "function_tools_executed",
  SPEECH_CREATED = "speech_created",
}

/**
 * LiveKit agents-js 1.4.11 (agents-js PR #1804) switched `sessionReportToJSON()`'s
 * output — the `events` array and the `chat_history` items — from camelCase to
 * snake_case, to match the Python session-report schema. It runs the payload
 * through a deep, generic `toSnakeCaseDeep` (every object key snake_cased), with
 * one special case: the chat-item `args` field is emitted as `arguments`.
 *
 * `normalizeLivekitReportKeys` is the inverse: a generic deep snake_case →
 * camelCase conversion of object keys, undoing the `args`→`arguments` rename and
 * mirroring LiveKit's `extra` passthrough (its inner keys are left untouched). We
 * invert generically rather than whitelisting specific fields so that every
 * multi-word key survives — not just the ones this repo happens to read today
 * (e.g. message `metrics.{llmNodeTtft,ttsNodeTtfb,e2eLatency}` feed the latency
 * report; missing them silently emptied it) and any field a future SDK bump adds.
 *
 * Keys already in camelCase have no internal `snake_case` boundary to rewrite, so
 * the pre-1.4.11 wire format and already-persisted (camelCase) transcripts pass
 * through unchanged — the conversion is a no-op on them and is idempotent.
 */
function snakeKeyToCamel(key: string): string {
  // Undo LiveKit's one non-mechanical rename (chat-item `args` -> `arguments`).
  // Note this is applied at every depth (except inside `extra`, which the caller
  // skips), so a nested key literally named `arguments` anywhere in the report
  // would also be rewritten. That's fine for the current payload — `arguments`
  // only appears as the function-call field — but is worth knowing if a future SDK
  // emits `arguments` elsewhere.
  if (key === "arguments") {
    return "args";
  }

  // Generic snake_case -> camelCase. The lookbehind asserts (without consuming) an
  // alphanumeric before the underscore run, which matters in two ways:
  //  - Consecutive single-char segments like `a_b_c` convert fully. Because the
  //    preceding char is asserted against the original string rather than consumed
  //    by the match, it can still anchor the next boundary (`a_b_c` -> `aBC`); a
  //    `(char)_(char)` form would eat the separator and leave `aB_c`.
  //  - A leading underscore has nothing before it, so it's left intact — e.g. the
  //    private `_chatItems` key on legacy speech_created events stays `_chatItems`.
  // `_+` collapses any run of underscores onto a single camel boundary.
  return key.replace(/(?<=[a-z0-9])_+([a-z0-9])/g, (_match, char: string) => char.toUpperCase());
}

export function normalizeLivekitReportKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeLivekitReportKeys);
  }

  if (value === null || typeof value !== "object") {
    return value;
  }

  const normalized: Record<string, unknown> = {};

  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    const camelKey = snakeKeyToCamel(key);

    // `extra` is a provider-specific passthrough LiveKit intentionally leaves
    // untouched when serializing; mirror that and don't rewrite its inner keys.
    normalized[camelKey] = camelKey === "extra" ? val : normalizeLivekitReportKeys(val);
  }

  return normalized;
}

function normalizeLivekitChatItemTimestamp(value: unknown): unknown {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return value;
  }

  const item = value as Record<string, unknown>;

  if (!("createdAt" in item)) {
    return item;
  }

  const createdAt = normalizeLivekitTimestampMs(item.createdAt);

  return createdAt === null ? item : { ...item, createdAt };
}

export function normalizeLivekitChatItem(value: unknown): unknown {
  return normalizeLivekitChatItemTimestamp(normalizeLivekitReportKeys(value));
}

export function normalizeLivekitChatHistory(value: unknown): unknown {
  const normalized = normalizeLivekitReportKeys(value);

  if (Array.isArray(normalized)) {
    return normalized.map(normalizeLivekitChatItemTimestamp);
  }

  if (normalized === null || typeof normalized !== "object") {
    return normalized;
  }

  const history = normalized as Record<string, unknown>;

  return Array.isArray(history.items)
    ? { ...history, items: history.items.map(normalizeLivekitChatItemTimestamp) }
    : history;
}

export const rawSessionChatItemSchema = z
  .object({
    args: z.string().nullish(),
    callId: z.string().nullish(),
    content: z.array(z.string()).nullish(),
    id: z.string().nullish(),
    isError: z.boolean().nullish(),
    name: z.string().nullish(),
    output: z.string().nullish(),
    role: z.nativeEnum(ChatItemRole).nullish(),
    type: z.nativeEnum(ChatItemType),
  })
  .passthrough();

export type ChatItem = z.infer<typeof rawSessionChatItemSchema>;

export const conversationItemAddedEventSchema = z
  .object({
    createdAt: z.number().nullish(),
    item: rawSessionChatItemSchema,
    type: z.literal(EventType.CONVERSATION_ITEM_ADDED),
  })
  .passthrough();

export const functionToolsExecutedEventSchema = z
  .object({
    createdAt: z.number().nullish(),
    functionCallOutputs: z.array(rawSessionChatItemSchema).optional().default([]),
    functionCalls: z.array(rawSessionChatItemSchema).optional().default([]),
    type: z.literal(EventType.FUNCTION_TOOLS_EXECUTED),
  })
  .passthrough();

export const speechCreatedEventSchema = z
  .object({
    createdAt: z.number().nullish(),
    // LiveKit 1.4.11 drops `speechHandle` from serialized `speech_created` events
    // (it deletes the handle before serializing), so this is optional now. The
    // chat items it used to carry are duplicated in the conversation_item_added /
    // function_tools_executed events, so nothing downstream is lost.
    speechHandle: z
      .object({
        _chatItems: z.array(rawSessionChatItemSchema).optional().default([]),
      })
      .passthrough()
      .optional(),
    type: z.literal(EventType.SPEECH_CREATED),
  })
  .passthrough();

// Unlike chat-history item timestamps, LiveKit leaves session-event timestamps
// in milliseconds during serialization. Do not pass these through the
// second-or-millisecond timestamp normalizer.
export const sessionEventSchema = z.discriminatedUnion("type", [
  conversationItemAddedEventSchema,
  functionToolsExecutedEventSchema,
  speechCreatedEventSchema,
]);

const unknownSessionEventSchema = z
  .object({
    createdAt: z.number().nullish(),
    type: z.string(),
  })
  .passthrough();

const RELEVANT_SESSION_EVENT_TYPES = new Set<string>(Object.values(EventType));

export const sessionEventsSchema = z
  .array(z.preprocess(normalizeLivekitReportKeys, z.union([sessionEventSchema, unknownSessionEventSchema])))
  .transform((events) => events.filter((event): event is SessionEvent => RELEVANT_SESSION_EVENT_TYPES.has(event.type)));

export type RawSessionChatItem = z.infer<typeof rawSessionChatItemSchema>;
export type SessionEvent = z.infer<typeof sessionEventSchema>;
